import he from "he";
import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom/client";
import styled from "styled-components";

import { useDividendAuctionPrice, useEstimatedDividend, useLesseeDividendCaseNo, useAuctionDetail } from "./api/index";
import { AuctionStatusInstInfo } from "./components/AuctionStatusInstInfo";
import pdfIcon from "./ui/PDFIcon.png";

import { StringParser } from "../StringParser";
import AuctionExpectedOrderPreview from "./AuctionExpectedOrderPreview";
import AuctionPricePreview from "./AuctionPricePreview";
import AuctionRightCheckPreview from "./AuctionRightCheckPreview";
import AuctionSaleItemPreview from "./AuctionSaleItemPreview";
import { IRightCheck } from "./lib/DataTable.type";
import LinkIcon from "./ui/LinkIcon";
import ArrowIcon from "@src/assets/ArrowIcon";
import { useLocation, useNavigate } from "react-router-dom";
import { transferToNative } from "@src/lib/WebAppBridge";

interface FileData {
    name: string;
    url: string;
}

interface OwnProps {
    value: string;
    fileData: FileData[];
}

const DataTable = ({ value, fileData }: OwnProps) => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const itemId = searchParams.get("itemId") ?? "";

    const [isBoxVisible, setIsBoxVisible] = useState(0);

    const { data: auctionDetail } = useAuctionDetail(itemId);
    const { data: auctionPriceNoDataMessageType } = useLesseeDividendCaseNo(itemId ?? "");

    // 임차인 현황
    const rightCheck: IRightCheck | undefined = auctionDetail?.payload?.result?.tab_right_check?.list?.[0];

    // 매각물건 현황
    const jibun = auctionDetail?.payload?.result?.section_initial?.adrs_jibun;
    const saleItem = auctionDetail?.payload?.result?.tab_sale_items?.list?.[0];

    //경매비용
    const { data: auctionPrice } = useDividendAuctionPrice(itemId ?? "");

    // 예상배당순서
    const { data: estimatedDividend } = useEstimatedDividend(itemId ?? "");

    // 컴포넌트 렌더링 맵 (내부 토글용)
    const componentMap = useMemo(
        () => ({
            1: rightCheck ? <AuctionRightCheckPreview auctionData={rightCheck} /> : null,
            2: saleItem ? <AuctionSaleItemPreview auctionData={saleItem} jibun={jibun} /> : null,
            3: <AuctionPricePreview auctionData={{ auctionPrice, auctionPriceNoDataMessageType }} />,
            4: estimatedDividend ? (
                <AuctionExpectedOrderPreview auctionData={{ estimatedDividend, auctionPriceNoDataMessageType }} />
            ) : null,
        }),
        [rightCheck, saleItem, jibun, auctionPrice, auctionPriceNoDataMessageType, estimatedDividend],
    );

    const handleFileClick = (file: FileData) => {
        const isToggleButton = file.url.startsWith("&");

        if (isToggleButton) {
            const fileNumber = parseInt(file.url.replace("&", ""), 10);
            if (fileNumber === 5) {
                transferToNative(JSON.stringify({ type: "CurrentStatus" }), "onMissgoAIClicked");
            } else {
                setIsBoxVisible((prev) => (prev === fileNumber ? 0 : fileNumber));
            }
        } else {
            navigate(`/missgoAI/viewer?file=${file.url}&title=${file.name}&backItemId=${searchParams.get("itemId")}`);
        }
    };

    const VisibleComponent = componentMap[isBoxVisible as keyof typeof componentMap] || null;

    return (
        <DataTableContainer>
            <strong>{StringParser(value)}</strong>
            <FileListBox>
                {Array.isArray(fileData) &&
                    fileData.map((file, index) => {
                        const isToggleButton = file.url.startsWith("&");
                        const fileNumber = isToggleButton ? parseInt(file.url.replace("&", ""), 10) : 0;

                        return (
                            <FileListItem key={index} $isToggleButton={isToggleButton} onClick={() => handleFileClick(file)}>
                                {!isToggleButton && <img src={pdfIcon} alt="pdfIcon" />}
                                {file.name}
                                {isToggleButton && fileNumber !== 5 && (
                                    <ArrowIcon direction={isBoxVisible === fileNumber ? "down" : "right"} />
                                )}
                                {isToggleButton && fileNumber === 5 && <LinkIcon />}
                            </FileListItem>
                        );
                    })}
            </FileListBox>

            {isBoxVisible !== 0 && <ToggledBox>{VisibleComponent}</ToggledBox>}
        </DataTableContainer>
    );
};

const DataTableContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;
`;

const ToggledBox = styled.div`
    margin-top: 8px;

    p {
        margin: 0;
        color: #333;
    }
`;

const FileListBox = styled.div`
    display: flex;
    flex-wrap: wrap;
`;

const FileListItem = styled.button<{ $isToggleButton: boolean }>`
    gap: 8px;
    display: inline-flex;
    border: none;
    padding: 8px 12px;
    border-radius: 4px;
    cursor: pointer;
    margin-right: 8px;
    margin-bottom: 8px;
    transition: background-color 0.2s;
    font-weight: bold;
    text-decoration: none;
    align-items: center;
    color: white;
    height: 34px;

    background-color: ${(props) => (props.$isToggleButton ? "#007bff" : "#C3C3C3")};

    &:hover {
        background-color: ${(props) => (props.$isToggleButton ? "#007bff" : "#C3C3C3")};
    }
`;

export default DataTable;
